/**
 * useCompany — reads company branding from the current user's tenant record
 * (`tenants.logo_url` / `name`, via GET /api/auth/tenant). Falls back to safe
 * platform defaults if the request fails, and for super_admin, which has no
 * tenant.
 *
 * A module-level cache with a listener set rather than a React context: the
 * shell is the only frequent consumer, and this keeps branding out of the
 * provider stack. The cache is shared across every consumer, so one fetch
 * serves them all.
 */
import { useEffect, useState } from 'react';
import { apiClient } from '../services/api';

export interface CompanyBranding {
  name: string;
  short: string;
  logoUrl: string | null;
  loading: boolean;
}

const DEFAULTS: CompanyBranding = {
  name: 'Attendza',
  short: 'AT',
  logoUrl: null,
  loading: true,
};

let _cache: CompanyBranding | null = null;
let _inFlight: Promise<void> | null = null;
const _listeners = new Set<(next: CompanyBranding) => void>();

function notifyListeners() {
  const next = _cache ?? DEFAULTS;
  _listeners.forEach((fn) => fn(next));
}

/**
 * Fetch branding into the cache and publish it. De-duplicated: concurrent
 * callers (several consumers mounting at once, or a mount racing an
 * invalidation) share one request.
 */
function fetchBranding(): Promise<void> {
  if (_inFlight) return _inFlight;

  _inFlight = apiClient
    .get<{ success: boolean; data: { name: string; short: string; logoUrl: string | null } }>(
      '/api/auth/tenant',
    )
    .then((res) => {
      const { name, short, logoUrl } = res.data.data;
      _cache = { name, short, logoUrl, loading: false };
    })
    .catch(() => {
      _cache = { ...DEFAULTS, loading: false };
    })
    .finally(() => {
      _inFlight = null;
      notifyListeners();
    });

  return _inFlight;
}

/**
 * Drop the cache and refetch, publishing the result to every mounted consumer.
 *
 * The refetch is the point. This used to null the cache and notify, but the
 * listener ignored a null cache and the fetch was guarded by `if (!_cache)`
 * inside a mount effect that never re-ran — so a branding change did not appear
 * until something remounted, which made saving company settings look broken.
 */
export function invalidateCompanyCache() {
  _cache = null;
  void fetchBranding();
}

/**
 * Publish a known-good logo URL without a round-trip, so an upload repaints the
 * shell the instant the response lands. Pass null to fall back to the monogram.
 */
export function applyCompanyLogo(logoUrl: string | null) {
  _cache = { ...(_cache ?? { ...DEFAULTS, loading: false }), logoUrl, loading: false };
  notifyListeners();
}

export function useCompany(): CompanyBranding {
  const [branding, setBranding] = useState<CompanyBranding>(_cache ?? DEFAULTS);

  useEffect(() => {
    let cancelled = false;

    function update(next: CompanyBranding) {
      if (!cancelled) setBranding(next);
    }
    _listeners.add(update);

    if (_cache) setBranding(_cache);
    else void fetchBranding();

    return () => {
      _listeners.delete(update);
      cancelled = true;
    };
  }, []);

  return branding;
}
